import type { SessionType } from "@mvk/shared";
import type { EventSession } from "../types";
import { isApiEnabled } from "../api/config";
import { visitorAuthStorage } from "../api/authStorage";
import { sessionsApi, sessionDtoToUiSession } from "../api/sessions";

function requireApiAuth(): void {
  if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated()) {
    throw new Error("[sessionsService] API auth required");
  }
}

export const sessionsService = {
  async getByEvent(eventId: string): Promise<EventSession[]> {
    requireApiAuth();
    const data = await sessionsApi.list(eventId);
    return data.sessions.map(sessionDtoToUiSession);
  },

  async getById(eventId: string, id: string): Promise<EventSession | undefined> {
    requireApiAuth();
    try {
      const data = await sessionsApi.get(eventId, id);
      return sessionDtoToUiSession(data.session);
    } catch {
      return undefined;
    }
  },

  async getByDate(eventId: string, date: string): Promise<EventSession[]> {
    const sessions = await this.getByEvent(eventId);
    return sessions.filter((s) => {
      const sessionDate = new Date(s.startTime).toISOString().split("T")[0];
      return sessionDate === date;
    });
  },

  async getByType(eventId: string, type: SessionType): Promise<EventSession[]> {
    const sessions = await this.getByEvent(eventId);
    return sessions.filter((s) => s.type === type);
  },

  async getUpcoming(eventId: string, limit = 5): Promise<EventSession[]> {
    const now = new Date();
    return (await this.getByEvent(eventId))
      .filter((s) => new Date(s.startTime) > now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, limit);
  },

  async getDates(eventId: string): Promise<string[]> {
    const dates = new Set<string>();
    (await this.getByEvent(eventId)).forEach((s) => {
      dates.add(new Date(s.startTime).toISOString().split("T")[0]!);
    });
    return Array.from(dates).sort();
  },

  async addToFavorites(_userId: string, sessionId: string, eventId: string): Promise<void> {
    requireApiAuth();
    await sessionsApi.addFavorite(eventId, sessionId);
  },

  async setFavoriteNotification(
    _userId: string,
    eventId: string,
    sessionId: string,
    notificationEnabled: boolean,
  ): Promise<void> {
    requireApiAuth();
    await sessionsApi.setFavoriteNotification(eventId, sessionId, notificationEnabled);
  },

  async removeFromFavorites(_userId: string, sessionId: string, eventId: string): Promise<void> {
    requireApiAuth();
    await sessionsApi.removeFavorite(eventId, sessionId);
  },

  async isFavorite(eventId: string, _userId: string, sessionId: string): Promise<boolean> {
    requireApiAuth();
    try {
      const data = await sessionsApi.get(eventId, sessionId);
      return data.session.isFavorite ?? false;
    } catch {
      return false;
    }
  },

  async getUserFavorites(_userId: string, eventId: string): Promise<EventSession[]> {
    requireApiAuth();
    const data = await sessionsApi.list(eventId, { favorites: true });
    return data.sessions.map(sessionDtoToUiSession);
  },

  async search(query: string, eventId: string): Promise<EventSession[]> {
    try {
      requireApiAuth();
      if (typeof navigator !== "undefined" && navigator.onLine === false) return [];
      const data = await sessionsApi.list(eventId, { q: query });
      return data.sessions.map(sessionDtoToUiSession);
    } catch {
      return [];
    }
  },
};
